import { createContext, useCallback, useContext, useState } from 'react';

import type { FunctionComponent, PropsWithChildren } from 'react';

type NavigationMenuContextValue = {
    readonly closeMenu: () => void;
    readonly isOpen: boolean;
    readonly openMenu: () => void;
    readonly toggleMenu: () => void;
};

const NavigationMenuContext = createContext<NavigationMenuContextValue | null>(null);

const STORAGE_KEY = 'navigation-menu-open';

const getStoredMenuState = (): boolean => {
    try {
        const stored = globalThis.localStorage.getItem(STORAGE_KEY);

        if (stored === 'false') {
            return false;
        }

        return true;
    } catch {
        return true;
    }
};

const saveMenuState = (isOpen: boolean): void => {
    try {
        globalThis.localStorage.setItem(STORAGE_KEY, String(isOpen));
    } catch {
        // localStorage not available, silently ignore
    }
};

export const NavigationMenuProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const [isOpen, setIsOpen] = useState<boolean>(getStoredMenuState);

    const closeMenu = useCallback((): void => {
        setIsOpen(false);
        saveMenuState(false);
    }, []);

    const openMenu = useCallback((): void => {
        setIsOpen(true);
        saveMenuState(true);
    }, []);

    const toggleMenu = useCallback((): void => {
        setIsOpen((prev) => {
            const newState = !prev;
            saveMenuState(newState);

            return newState;
        });
    }, []);

    return (
        <NavigationMenuContext.Provider value={{ closeMenu, isOpen, openMenu, toggleMenu }}>
            {children}
        </NavigationMenuContext.Provider>
    );
};

export const useNavigationMenu = (): NavigationMenuContextValue => {
    const context = useContext(NavigationMenuContext);

    if (context === null) {
        throw new Error('useNavigationMenu must be used within NavigationMenuProvider');
    }

    return context;
};
